import { cloneElement, useEffect, useLayoutEffect, useRef, Children, isValidElement } from 'react'
import PropTypes from 'prop-types'
import {
  useFloating,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  useTransitionStyles,
  offset,
  flip,
  shift,
  FloatingPortal,
} from '@floating-ui/react'
import styles from './Dropdown.module.css'

const PILL_COLORS = ['default', 'rojo', 'verde', 'amarillo', 'celeste']

function DropdownItem({
  children,
  description,
  number,
  pill,
  pillColor = 'default',
  checked,
  onCheckedChange,
  active = false,
  disabled = false,
  onClick,
  className = '',
}) {
  const isCheckable = checked !== undefined

  const handleClick = (e) => {
    if (disabled) return
    if (isCheckable) onCheckedChange?.(!checked)
    onClick?.(e)
  }

  const classes = [
    styles['dropdown-item'],
    active || checked ? styles['dropdown-item--active'] : '',
    disabled ? styles['dropdown-item--disabled'] : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      role="option"
      aria-selected={isCheckable ? checked : active}
      disabled={disabled}
      className={classes}
      onClick={handleClick}
    >
      {isCheckable && (
        <span className={styles['dropdown-item__checkbox']}>
          <span className="material-icons-outlined">
            {checked ? 'check_box' : 'check_box_outline_blank'}
          </span>
        </span>
      )}
      <div className={styles['dropdown-item__content']}>
        {number !== undefined ? (
          <div className={styles['dropdown-item__title-row']}>
            <span className={styles['dropdown-item__title']}>{children}</span>
            <span className={styles['dropdown-item__number']}>{number}</span>
          </div>
        ) : (
          <span className={styles['dropdown-item__title']}>{children}</span>
        )}
        {description && <span className={styles['dropdown-item__desc']}>{description}</span>}
      </div>
      {pill && (
        <span
          className={[styles['dropdown-item__pill'], styles[`dropdown-item__pill--${pillColor}`]]
            .filter(Boolean)
            .join(' ')}
        >
          {pill}
        </span>
      )}
    </button>
  )
}

DropdownItem.propTypes = {
  children: PropTypes.node.isRequired,
  description: PropTypes.node,
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pill: PropTypes.node,
  pillColor: PropTypes.oneOf(PILL_COLORS),
  checked: PropTypes.bool,
  onCheckedChange: PropTypes.func,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
}

function DropdownGroupTitle({ children, description }) {
  return (
    <div className={styles['dropdown-item__group-title']}>
      <span className={styles['dropdown-item__group-title-text']}>{children}</span>
      {description && <span className={styles['dropdown-item__group-desc']}>{description}</span>}
    </div>
  )
}

DropdownGroupTitle.propTypes = {
  children: PropTypes.node.isRequired,
  description: PropTypes.node,
}

function DropdownSectionTitle({ children, description, checked, onCheckedChange }) {
  const hasCheckbox = checked !== undefined
  const classes = [
    styles['dropdown-item__section-title'],
    description ? styles['dropdown-item__section-title--desc'] : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes}>
      {hasCheckbox && (
        <span
          className={styles['dropdown-item__checkbox']}
          role="checkbox"
          aria-checked={checked}
          tabIndex={0}
          onClick={() => onCheckedChange?.(!checked)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onCheckedChange?.(!checked)
            }
          }}
        >
          <span className="material-icons-outlined">
            {checked ? 'check_box' : 'check_box_outline_blank'}
          </span>
        </span>
      )}
      <span className={styles['dropdown-item__section-title-text']}>{children}</span>
      {description && <span className={styles['dropdown-item__section-desc']}>{description}</span>}
    </div>
  )
}

DropdownSectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
  description: PropTypes.node,
  checked: PropTypes.bool,
  onCheckedChange: PropTypes.func,
}

function DropdownLoading({ children = 'Cargando...' }) {
  return (
    <div className={[styles['dropdown-item'], styles['dropdown-item--loading']].join(' ')}>
      <span className={styles['dropdown-item__status']}>{children}</span>
    </div>
  )
}
DropdownLoading.propTypes = { children: PropTypes.node }

function DropdownNoMatches({ children = 'No hay coincidencias' }) {
  return (
    <div className={[styles['dropdown-item'], styles['dropdown-item--no-matches']].join(' ')}>
      <span className={styles['dropdown-item__status']}>{children}</span>
    </div>
  )
}
DropdownNoMatches.propTypes = { children: PropTypes.node }

function DropdownFooter({ children }) {
  return <div className={styles.dropdown__footer}>{children}</div>
}
DropdownFooter.propTypes = { children: PropTypes.node.isRequired }

export function Dropdown({
  trigger,
  open,
  onOpenChange,
  width = 380,
  maxVisibleItems = 5,
  className = '',
  children,
}) {
  const listRef = useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange,
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'listbox' })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 150,
    initial: { opacity: 0, transform: 'translateY(-6px)' },
  })

  useEffect(() => {
    if (open && listRef.current) {
      const firstOption = listRef.current.querySelector('[role="option"]:not(:disabled)')
      firstOption?.focus()
    }
  }, [open])

  // Mide en tiempo real la altura de los primeros N items ya renderizados y la usa
  // como tope de la lista - asi "N visibles antes del scroll" funciona sea cual sea
  // el tipo de item (simple, con descripcion, con checkbox, etc.), sin un numero
  // fijo en CSS que solo daria N para un tipo de item puntual.
  // requestAnimationFrame de por medio: da un frame extra para que el layout este
  // completamente asentado (ancho del panel ya calculado por floating-ui, etc.)
  // antes de medir - si se mide demasiado pronto, offsetHeight puede no ser el final.
  useLayoutEffect(() => {
    if (!open) return

    const frame = requestAnimationFrame(() => {
      const list = listRef.current
      if (!list) return

      const allRows = Array.from(list.children)
      // Menos items que el maximo visible: sin tope, altura libre, nunca hay scroll.
      // Sumar alturas redondeadas individuales puede diferir en menos de 1px del alto
      // real renderizado, y alcanza esa diferencia para disparar un scroll de mas.
      if (allRows.length <= maxVisibleItems) {
        list.style.maxHeight = ''
        return
      }
      const rows = allRows.slice(0, maxVisibleItems)
      const height = rows.reduce((sum, el) => sum + el.offsetHeight, 0)
      list.style.maxHeight = `${height}px`
    })

    return () => cancelAnimationFrame(frame)
  }, [open, isMounted, maxVisibleItems, children])

  const handleKeyDown = (e) => {
    const container = listRef.current
    if (!container) return
    const options = Array.from(container.querySelectorAll('[role="option"]:not(:disabled)'))
    if (options.length === 0) return
    const currentIndex = options.indexOf(document.activeElement)

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      options[(currentIndex + 1 + options.length) % options.length].focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      options[(currentIndex - 1 + options.length) % options.length].focus()
    } else if (e.key === 'Home') {
      e.preventDefault()
      options[0].focus()
    } else if (e.key === 'End') {
      e.preventDefault()
      options[options.length - 1].focus()
    }
  }

  const childArray = Children.toArray(children)
  const footerChild = childArray.find(
    (child) => isValidElement(child) && child.type === DropdownFooter
  )
  const listChildren = childArray.filter(
    (child) => !(isValidElement(child) && child.type === DropdownFooter)
  )

  return (
    <>
      {cloneElement(trigger, {
        ref: refs.setReference,
        ...getReferenceProps(trigger.props),
        'aria-haspopup': 'listbox',
      })}
      {isMounted && (
        <FloatingPortal>
          {/* Div de afuera: solo posicionamiento (floating-ui usa `transform` para esto).
              Div de adentro: solo la transicion de fade/slide (tambien usa `transform`) -
              separados para que no se pisen entre si. */}
          <div ref={refs.setFloating} style={{ ...floatingStyles, display: 'inline-block' }}>
            <div
              style={{ ...transitionStyles, width }}
              className={[styles.dropdown, className].filter(Boolean).join(' ')}
              onKeyDown={handleKeyDown}
              {...getFloatingProps()}
            >
              <div ref={listRef} className={styles.dropdown__list}>
                {listChildren}
              </div>
              {footerChild}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

Dropdown.propTypes = {
  trigger: PropTypes.element.isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  width: PropTypes.number,
  maxVisibleItems: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
}

Dropdown.Item = DropdownItem
Dropdown.GroupTitle = DropdownGroupTitle
Dropdown.SectionTitle = DropdownSectionTitle
Dropdown.Loading = DropdownLoading
Dropdown.NoMatches = DropdownNoMatches
Dropdown.Footer = DropdownFooter
